import React, { useEffect, useState, useRef } from "react";
import { ColumnType } from "antd/es/table";
import {
  Form,
  Input,
  Popconfirm,
  Table,
  Button as Buttons,
  Select,
  notification,
  Space,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  LockOutlined,
  StopOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import DocumentsController from "./Controller/document_controller.ts";
import CustomSeparator from "./../../layouts/AdminLayout/Header/Breadcrumbs.js";
import Typography from "@mui/material/Typography";
import type { InputRef, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { createStyles } from "antd-style";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
} from "@mui/material";
import { Link } from "react-router-dom";
import SecretariatController from "./Controller/secretariat_controller.ts";
import CorporateMemberController from "./Controller/corporate_membership_controller.ts";
import TextArea from "antd/es/input/TextArea";
import InfinityEncryptor from "../../components/EncryptDecrypt/InfinityEncryptor.js";

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

type NotificationType = "success" | "info" | "warning" | "error";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface DataType {
  key: string;
  userID: string;
  name: string;
  nric: string;
  handphone_no: string;
  email: string;
  correspondence_address: string;
  registered_address: string;
  status: string;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType:
    | "select"
    | "text"
    | "date"
    | "readonly"
    | "number_of_years_with_company"
    | "academic_qualification"
    | "handphone_no"
    | "email"
    | "account_type"
    | "committee_typeID";
  record: DataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  let inputNode: any;
  let required_field = true;

  if (inputType === "select") {
    required_field = false;

    inputNode = (
      <Select>
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else if (inputType === "academic_qualification") {
    required_field = false;

    inputNode = (
      <Select>
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else if (inputType === "date") {
    required_field = false;

    inputNode = <Input type="date" />;
  } else if (inputType === "readonly") {
    required_field = false;
    inputNode = <Input disabled />;
  } else if (inputType === "email") {
    required_field = true;
    inputNode = <TextArea autoSize={{ minRows: 2, maxRows: 10 }} />;
  } else if (inputType === "handphone_no") {
    required_field = true;
    inputNode = <TextArea autoSize={{ minRows: 2, maxRows: 10 }} />;
  } else {
    required_field = false;
    inputNode = <TextArea autoSize={{ minRows: 2, maxRows: 10 }} />;
  }
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: required_field,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const CheckboxList = ({ columns, onChange, checkedColumns }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // State for checkboxes, synchronized with `checkedColumns` from parent
  const [checked, setChecked] = useState(checkedColumns);

  useEffect(() => {
    setChecked(checkedColumns); // Synchronize state with prop
  }, [checkedColumns]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    onChange(newChecked); // Notify parent about changes
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {columns.map((value) => {
            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={checked.includes(value.dataIndex)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

const Secretariat: React.FC = () => {
  // encryptor
  const key = "12345";
  const mode = "YMDHI";
  const Encryptor = new InfinityEncryptor(key, mode);

  const current_userId = localStorage.getItem("userID");

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (
    type: NotificationType,
    desc: string,
    message: String
  ) => {
    api[type]({
      message: message,
      description: desc,
    });
  };

  const [form] = Form.useForm();
  const [rows, setRows] = useState<any[]>([]);
  const [shouldRefetchData, setShouldRefetchData] = useState(false);

  useEffect(() => {
    SecretariatController.getSecretariat().then((response) => {
      const row = response.map((item: any, index: any) => ({
        key: index + 1,
        name: item.name,
        nric: item.nric,
        handphone_no: item.handphone_no,
        email: item.email,
        correspondence_address: item.correspondence_address,
        registered_address: item.registered_address,
        userID: item.userID,
        status: item.status,
      }));

      setRows(row);
    });
  }, [shouldRefetchData]);

  // Update data whenever rows changes
  useEffect(() => {
    setRows(rows);
  }, [rows]);

  const [editingKey, setEditingKey] = useState("");

  const userID = localStorage.getItem("userID");

  const isEditing = (record: DataType) => record.key === editingKey;

  const edit = (record: Partial<DataType> & { key: React.Key }) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const delete_record = (rowId: string) => {
    const payload = new FormData();
    payload.append("documentID", rowId); // Append event ID
    DocumentsController.deleteDocuments(payload).then((response) => {
      console.log(response);
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Your data has been deleted successfully!",
          "Success"
        );

        setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
      }
    });
  };

  const deactivateMember = (rowId: any, userID) => {
    const payload = new FormData();
    payload.append("ID", rowId);
    payload.append("userID", userID);

    SecretariatController.deactivateMembership(payload).then((response) => {
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Membership has been deactivated successfully!",
          "Success"
        );

        setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
      }
    });
  };

  const reactivateMember = (rowId: any, userID) => {
    const payload = new FormData();
    payload.append("ID", rowId);
    payload.append("userID", userID);

    SecretariatController.reactivateMembership(payload).then((response) => {
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Membership has been reactivated successfully!",
          "Success"
        );

        setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
      }
    });
  };

  const save = async (key: React.Key, userID) => {
    try {
      const rowEdited = (await form.validateFields()) as DataType;
      const newData = [...rows];
      const index = newData.findIndex((item) => {
        return key === item.userID;
      });

      console.log(rowEdited);
      if (index > -1) {
        const item = newData[index];
        const edit_userID = item.userID;

        const payload = new FormData();
        payload.append("userID", userID);
        payload.append("ID", Encryptor.Encrypt(edit_userID));
        Object.keys(rowEdited).forEach((field) => {
          const encryptedValue = Encryptor.Encrypt(rowEdited[field]); // Encrypt value
          payload.append(`data[${field}]`, encryptedValue); // Append encrypted value
        });
        SecretariatController.editSecretariat(payload).then((response) => {
          console.log(response);
          if (response?.status === "success") {
            openNotificationWithIcon(
              "success",
              "Your data has been saved successfully!",
              "Success"
            );
            // Handle successful response and update rows or UI
            setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
          }
        });
        setRows(newData);
        setEditingKey("");
      } else {
        newData.push(rowEdited);
        setRows(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const handleQueryString = (path: string, state: Record<string, string>) => {
    const queryString = new URLSearchParams(state).toString();
    return `${path}?${queryString}`;
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "key",
      editable: false,
      width: "5%",
    },
    {
      title: "Name",
      dataIndex: "name",
      editable: true,
    },
    {
      title: "NRIC",
      dataIndex: "nric",
      editable: true,
    },
    {
      title: "Mobile No.",
      dataIndex: "handphone_no",
      editable: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      editable: true,
    },
    {
      title: "Correspondence Address",
      dataIndex: "correspondence_address",
      editable: true,
    },
    {
      title: "Registered Address",
      dataIndex: "registered_address",
      editable: true,
    },
    {
      title: "Operation",
      dataIndex: "operation",
      width: 180,
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        const pathPassword = "/admin/reset_password";
        const statePassword = { userID: record.userID };
        const fullPathPassword = handleQueryString(pathPassword, statePassword);
        return editable ? (
          <>
            <Buttons
              onClick={() => save(record.userID, userID)}
              color="default"
              variant="solid"
              style={{ marginBottom: "10px" }}
            >
              Save
            </Buttons>
            <Buttons color="default" variant="outlined">
              <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                Cancel
              </Popconfirm>
            </Buttons>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "90%",
            }}
          >
            <Buttons
              color="default"
              variant="solid"
              icon={<EditOutlined />}
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              style={{ marginBottom: "10px" }}
            >
              Edit
            </Buttons>
            <Buttons
              type="default"
              style={{ marginBottom: "10px" }}
              icon={<LockOutlined />}
            >
              <Link
                to={fullPathPassword}
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Password
              </Link>
            </Buttons>
            {/* <Buttons
              type="primary"
              danger
              icon={<DeleteOutlined />}
              disabled={editingKey !== "" || editingKey !== ""}
            >
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => delete_record(record.userID)}
              >
                Delete
              </Popconfirm>
            </Buttons> */}
            {record.userID && Number(record.status) === 1 && (
              <Buttons
                type="primary"
                style={{ marginBottom: "10px" }}
                danger
                icon={<StopOutlined />}
                disabled={editingKey !== ""}
              >
                <Popconfirm
                  title="Sure to suspend membership?"
                  onConfirm={() => deactivateMember(record.userID, userID)}
                >
                  Suspend
                </Popconfirm>
              </Buttons>
            )}

            {record.userID && Number(record.status) === 0 && (
              <Buttons
                type="primary"
                style={{
                  marginBottom: "10px",
                  backgroundColor: "#4CAF50", // Custom background color
                  color: "#FFFFFF", // Custom text color
                  borderColor: "#4CAF50", // Custom border color
                }}
                danger
                icon={<CheckOutlined />}
                disabled={editingKey !== ""}
              >
                <Popconfirm
                  title="Sure to activate membership?"
                  onConfirm={() => reactivateMember(record.userID, userID)}
                >
                  Unsuspend
                </Popconfirm>
              </Buttons>
            )}
          </div>
        );
      },
    },
  ];

  const [checkedColumns, setCheckedColumns] = useState([
    "key",
    "name",
    "nric",
    "handphone_no",
    "email",
    // "correspondence_address",
    // "datetime_expired",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  const mergedColumns = columns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: DataType) => ({
          record,
          inputType: "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

  const { styles } = useStyle();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "100%", // scroll y for page
          minHeight: "150vh",
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />
          <CustomSeparator pageName="secretariat" />
          {contextHolder}

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Secretariat Directory
          </Typography>
          <Stack spacing={2} direction="row" sx={{ mb: 3 }}>
            <CheckboxList
              columns={columns}
              checkedColumns={checkedColumns}
              onChange={onChange}
            />
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "100%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table<DataType>
                className={styles.customTable}
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                dataSource={rows}
                columns={mergedColumns as ColumnType<DataType>[]}
                rowClassName="editable-row"
                pagination={{ onChange: cancel }}
                scroll={{ x: "80vh", y: 150 * 5 }}
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Secretariat;
